import React, { useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import StatCard from "../components/ui/StatCard";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import FormField from "../components/ui/FormField";
import { statusColor } from "../utils/helpers";
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";
import { FiRefreshCw } from "react-icons/fi";
import "./Collection.css";

export default function Collection() {
  const API = "https://maize.norissolutions.in/Php/public/api";
  const token = sessionStorage.getItem("maaze_token");
  const { refreshToken, user } = useAuth();
  const { selectedSeason } = useSeason();

  /* ================= STATE ================= */

  const [collections, setCollections] = useState([]);
  const [villages, setVillages] = useState([]);
  const [stats, setStats] = useState({
    total_collected: 0,
    quality_approved: 0,
    pending_qc: 0,
    rejected: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [form, setForm] = useState({
    ID: null,
    Vehicle: "",
    Party: "",
    Village: "",
    Material: "",
    Transporter: "",
    Deduction: "",
    DCQty: "",
    Varity: "",
    PureWeight: "",
    Rate: "",
    Amount: ""
  });

  /* ================= FETCH DATA ================= */

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API}/weighbridge`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: user?.id,
          season_id: selectedSeason
        }
      });

      setCollections(res.data?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchVillages = async () => {
    try {
      const res = await axios.get(`${API}/villages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVillages(res.data?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(
        `${API}/dashboard/crop-collection-cards`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data?.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchVillages();
    fetchStats();
  }, [user?.id, selectedSeason]);

  /* ================= PARTIES ================= */

  const uniqueParties = [...new Set(collections.map((c) => c.Party).filter(Boolean))];

  /* ================= EDIT / SELECT ================= */

  const handleEdit = (lot) => {
    setForm({
      ID: lot.ID || lot.collection_id,
      Vehicle: lot.Vehicle || "",
      Party: lot.Party || "",
      Village: lot.Village || "",
      Material: lot.Material || "",
      Transporter: lot.Transporter || "",
      Deduction: lot.Deduction || "",
      DCQty: lot.DCQty || "",
      Varity: lot.Varity || "",
      PureWeight: lot.PureWeight || "",
      Rate: lot.Rate || "",
      Amount: lot.Amount || ""
    });
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {
    if (!form.ID) {
      alert("Please select a collection from the queue to update.");
      return;
    }

    const payload = {
      Vehicle: form.Vehicle,
      Party: form.Party,
      Village: form.Village,
      Material: form.Material,
      Transporter: form.Transporter,
      Deduction: form.Deduction,
      DCQty: form.DCQty,
      Varity: form.Varity,
      PureWeight: form.PureWeight,
      Rate: form.Rate,
      Amount: form.Amount,
    };

    try {
      await axios.post(`${API}/weighbridge/${form.ID}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      alert("Collection recorded successfully");

      fetchCollections();
      fetchStats();

      setForm({
        ID: null,
        Vehicle: "",
        Party: "",
        Village: "",
        Material: "",
        Transporter: "",
        Deduction: "",
        DCQty: "",
        Varity: "",
        PureWeight: "",
        Rate: "",
        Amount: ""
      });
    } catch (err) {
      console.log(err.response?.data || err);
      alert("Error saving collection");
    }
  };

  /* ================= UI ================= */

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCollections = collections.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(collections.length / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div>
      <PageHeader
        title="Crop Collection"
        subtitle="Record harvested crops and quality approval"
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <div className="stats-grid mb-24">
        <StatCard 
          label="Total Collected" 
          value={stats.total_collected} 
          unit="kg" 
          icon="📦" 
        />

        <StatCard
          label="Rejected"
          value={stats.rejected}
          unit="kg"
          icon="❌"
          variant="red"
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Card title="Collection Update details">
          {!form.ID && (
             <div style={{ marginBottom: 15, padding: 10, background: 'rgba(255, 193, 7, 0.1)', color: '#856404', borderRadius: 4, fontSize: 14 }}>
               Please select a collection record from the queue below to edit its details.
             </div>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', opacity: form.ID ? 1 : 0.5, pointerEvents: form.ID ? 'auto' : 'none' }}>
            <FormField
              label="Selected Weighbridge ID"
              type="text"
              value={form.ID || ""}
              disabled
              placeholder="Select from queue"
            />

            <FormField
              label="Vehicle"
              type="text"
              value={form.Vehicle}
              onChange={(e) => setForm({ ...form, Vehicle: e.target.value })}
            />

            <FormField
              label="Party (Organiser)"
              type="select"
              options={uniqueParties.map((party) => ({
                label: party,
                value: party,
              }))}
              value={form.Party}
              onChange={(e) => setForm({ ...form, Party: e.target.value })}
            />

            <FormField
              label="Village"
              type="select"
              options={villages.map((v) => ({
                label: v.village_name,
                value: v.village_name,
              }))}
              value={form.Village}
              onChange={(e) => setForm({ ...form, Village: e.target.value })}
            />

            <FormField
              label="Material"
              type="text"
              value={form.Material}
              onChange={(e) => setForm({ ...form, Material: e.target.value })}
            />

            <FormField
              label="Transporter"
              type="text"
              value={form.Transporter}
              onChange={(e) => setForm({ ...form, Transporter: e.target.value })}
            />

            <FormField
              label="Deduction"
              type="number"
              value={form.Deduction}
              onChange={(e) => setForm({ ...form, Deduction: e.target.value })}
            />

            <FormField
              label="DC Quantity"
              type="number"
              value={form.DCQty}
              onChange={(e) => setForm({ ...form, DCQty: e.target.value })}
            />

            <FormField
              label="Variety"
              type="text"
              value={form.Varity}
              onChange={(e) => setForm({ ...form, Varity: e.target.value })}
            />

            <FormField
              label="Pure Weight"
              type="number"
              value={form.PureWeight}
              onChange={(e) => setForm({ ...form, PureWeight: e.target.value })}
            />

            <FormField
              label="Rate"
              type="number"
              value={form.Rate}
              onChange={(e) => setForm({ ...form, Rate: e.target.value })}
            />

            <FormField
              label="Amount"
              type="number"
              value={form.Amount}
              onChange={(e) => setForm({ ...form, Amount: e.target.value })}
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button onClick={handleSubmit} disabled={!form.ID}>Update Collection</Button>

            <Button variant="outline" onClick={() => setForm({
              ID: null, Vehicle: "", Party: "", Village: "", Material: "", Transporter: "",
              Deduction: "", DCQty: "", Varity: "", PureWeight: "", Rate: "", Amount: ""
            })}>
              Clear
            </Button>
          </div>
        </Card>

        <Card title="Collection Queue">
          <div style={{ overflowX: 'auto' }}>
            {currentCollections.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--surface-50)' }}>
                    {Object.keys(currentCollections[0]).map(key => (
                      <th key={key} style={{ padding: '10px', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-muted)' }}>{key}</th>
                    ))}
                    <th style={{ padding: '10px', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-muted)' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCollections.map((lot, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      {Object.keys(currentCollections[0]).map(key => (
                        <td key={key} style={{ padding: '10px', whiteSpace: 'nowrap' }}>{lot[key] !== null ? String(lot[key]) : ""}</td>
                      ))}
                      <td style={{ padding: '10px' }}>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(lot)}>
                          Update
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                No collections found.
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {collections.length > itemsPerPage && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span style={{ fontSize: "14px", color: "var(--text-muted)" }}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
